import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router';
import axios from 'axios';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Switch,
    FormControlLabel,
    Divider,
    Alert,
    Stack,
    Tabs,
    Tab,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Link as LinkIcon,
    LinkOff as LinkOffIcon,
    Upload as UploadIcon,
    Launch as LaunchIcon,
    Euro as EuroIcon,
    People as PeopleIcon,
    Settings as SettingsIcon,
    Search as SearchIcon,
    Storage as StorageIcon,
    CloudQueue as CloudQueueIcon,
    Computer as ComputerIcon,
    Close as CloseIcon,
    PhotoCamera as PhotoCameraIcon,
    Category as CategoryIcon,
    PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { getPricingPlans, getPermissions, createPermission, updatePermission, deletePermission } from '../Utils/pricingApi';
import {
    applyDiscount,
    buildDiscountLabel,
    buildExpirationLabel,
    formatEUR,
    openBlankPrintWindow,
    writeAndPrint,
} from '../Utils/pdfExport';
import PdfExportDialog from '../Componant/PdfExport/PdfExportDialog';
import ConnectorServicesPanel from '../Componant/ConnectorServicesPanel';
import { buildProductSheetHtml } from '../Componant/PdfExport/productSheetTemplate';
import { getSettings } from '../Utils/settingsApi';

const PLAN_INTERVAL_LABEL = { day: 'jour', week: 'semaine', month: 'mois', year: 'an' };

const emptyPermissionForm = { key: '', name: '', description: '', visible_in_shop: true };

function describePlanPrice(plan, { ht = false } = {}) {
    if (!plan) return 'Sur devis';
    if (plan.type === 'quote') return 'Sur devis';
    if (plan.type === 'included') return 'Offert avec le compte';
    const amount = Number(plan.base_amount);
    if (!amount) return plan.name || 'Sur devis';
    const suffix = ht ? ' HT' : '';
    if (plan.type === 'subscription') {
        const interval = PLAN_INTERVAL_LABEL[plan.config?.interval] || 'mois';
        return `${formatEUR(amount)}${suffix} / ${interval}`;
    }
    if (plan.type === 'tiered' || plan.type === 'volume_grid') {
        return `À partir de ${formatEUR(amount)}${suffix}`;
    }
    return `${formatEUR(amount)}${suffix}`;
}

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

function TabPanel({ children, value, index, ...other }) {
    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}

export default function ApplicationManagementPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;
    const userPermissions = userInfo?.permissions || [];
    const canManageApp = isSuperAdmin || userPermissions.includes('applications.manage');
    const canDeleteApp = isSuperAdmin || userPermissions.includes('applications.delete');
    const canTechnical = isSuperAdmin || userPermissions.includes('applications.technical');
    const canDiscount = isSuperAdmin || userPermissions.includes('applications.pricing.discount');
    const [application, setApplication] = useState({
        client_id:             '',
        name:                  '',
        shortDescription:      '',
        description:           '',
        redirect_urls:         [],
        allow_all_redirect_url: false,
        isActive:              false,
        is_first_party:        false,
        is_internal:           false,
        auto_grant_on_signup:  false,
        requires_consent:      true,
        release_date:          null,
        image:                 '',
        icon:                  '',
        // Champs UI sans équivalent en BD (vides par défaut)
        url:                   '',
        version:               '',
        status:                '',
        category:              '',
        categories:            [],
        tags:                  [],
        features:              [],
        technicalInfo: {
            apiEndpoint:   '',
            documentation: '',
            dependencies:  [],
            servers:       [],
            clients:       [],
        },
    });
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [pdfExportOpen, setPdfExportOpen] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const [defaultManagementFeeLabel, setDefaultManagementFeeLabel] = useState('');
    const [appClients, setAppClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [appRelations, setAppRelations] = useState({ parent: null, children: [] });
    const [activeTab, setActiveTab] = useState(0);

    // Catalogue des permissions (droits de licence) que cette application peut accorder.
    // Consommé par les plans tarifaires (PricingPlansPanel) et par les licences client
    // (ClientManagementPage) pour restreindre leurs sélecteurs à des clés existantes.
    const [permissions, setPermissions] = useState([]);
    const [loadingPermissions, setLoadingPermissions] = useState(true);
    const [permissionsError, setPermissionsError] = useState(null);
    const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
    const [editingPermission, setEditingPermission] = useState(null);
    const [permissionForm, setPermissionForm] = useState(emptyPermissionForm);
    const [permissionFormError, setPermissionFormError] = useState('');
    const [openLinkClientDialog, setOpenLinkClientDialog] = useState(false);
    const [allClients, setAllClients] = useState([]);
    const [linkClientForm, setLinkClientForm] = useState({ id_client: '', dateDebut: '', dateFin: '' });
    const [linkingClient, setLinkingClient] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [screenshots, setScreenshots] = useState([]);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageError, setImageError] = useState(null);
    const [openServerDialog, setOpenServerDialog] = useState(false);
    const [editingServer, setEditingServer] = useState(null);
    const [serverForm, setServerForm] = useState({
        name: '',
        url: '',
        type: 'Production',
        status: 'Actif'
    });
    const [clientEnvironmentFilter, setClientEnvironmentFilter] = useState('all');

    // États pour la gestion des clients d'architecture
    const [openClientDialog, setOpenClientDialog] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [clientForm, setClientForm] = useState({
        name: '',
        url: '',
        environment: 'Production',
        platform: 'Web',
        version: '',
        status: 'Actif'
    });
    const [archClientEnvironmentFilter, setArchClientEnvironmentFilter] = useState('all');

    // États pour la gestion des catégories et groupes
    const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
    const [openGroupDialog, setOpenGroupDialog] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [groupForm, setGroupForm] = useState({
        name: '',
        description: '',
        color: 'primary.main'
    });

        // ── Load real application data from API ────────────────────────────────────
    useEffect(() => {
        if (!id || !accessToken) return;
        const headers = { Authorization: `Bearer ${accessToken}` };

        // Load application details
        axios.get(`${API_URL}/api/applications/${id}`, { headers })
            .then(res => {
                const d = res.data;
                setApplication(prev => ({
                    ...prev,
                    // Champs de base
                    client_id:              d.client_id              ?? prev.client_id,
                    name:                   d.name                   ?? prev.name,
                    shortDescription:       d.short_description      ?? prev.shortDescription,
                    description:            d.long_description       ?? prev.description,
                    redirect_urls:          d.redirect_urls          ?? prev.redirect_urls,
                    allow_all_redirect_url: d.allow_all_redirect_url ?? prev.allow_all_redirect_url,
                    isActive:               d.isActive               ?? prev.isActive,
                    is_first_party:         d.is_first_party         ?? prev.is_first_party,
                    is_internal:            d.is_internal            ?? prev.is_internal,
                    auto_grant_on_signup:   d.auto_grant_on_signup   ?? prev.auto_grant_on_signup,
                    requires_consent:       d.requires_consent       ?? prev.requires_consent,
                    release_date:           d.release_date           ?? prev.release_date,
                    image:                  d.image                  ?? prev.image,
                    icon:                   d.icon                   ?? prev.icon,
                    // Informations générales
                    url:                    d.url                    ?? prev.url,
                    version:                d.version                ?? prev.version,
                    category:               d.category               ?? prev.category,
                    tags:                   d.tags                   ?? prev.tags,
                    features:               d.features               ?? prev.features,
                    // Informations techniques
                    technicalInfo: d.technical_info && Object.keys(d.technical_info).length > 0
                        ? { ...prev.technicalInfo, ...d.technical_info }
                        : prev.technicalInfo,
                }));
            })
            .catch(err => console.error('Error loading application:', err.message));

        // Load linked clients
        setLoadingClients(true);
        axios.get(`${API_URL}/api/applications/${id}/clients`, { headers })
            .then(res => setAppClients(Array.isArray(res.data) ? res.data : []))
            .catch(err => console.error('Error loading clients:', err.message))
            .finally(() => setLoadingClients(false));

        // Load relations (parent / children)
        axios.get(`${API_URL}/api/applications/${id}/relations`, { headers })
            .then(res => setAppRelations(res.data || { parent: null, children: [] }))
            .catch(err => console.error('Error loading relations:', err.message));
    }, [id, accessToken]);

    // ── Load the default "frais de gestion" note (Réglages, Utils/settingsApi.js) ──
    useEffect(() => {
        if (!accessToken) return;
        getSettings(accessToken)
            .then((data) => setDefaultManagementFeeLabel(data.default_management_fee_label || ''))
            .catch(() => {});
    }, [accessToken]);

    // ── Permissions (droits de licence accordables par cette application) ──────
    const fetchPermissions = () => {
        if (!accessToken || !id) return;
        setLoadingPermissions(true);
        getPermissions(accessToken, id)
            .then(setPermissions)
            .catch(() => setPermissionsError('Impossible de charger les permissions'))
            .finally(() => setLoadingPermissions(false));
    };

    useEffect(() => { fetchPermissions(); }, [id, accessToken]);

    const openCreatePermission = () => {
        setEditingPermission(null);
        setPermissionForm(emptyPermissionForm);
        setPermissionFormError('');
        setPermissionDialogOpen(true);
    };

    const openEditPermission = (permission) => {
        setEditingPermission(permission);
        setPermissionForm({
            key: permission.key,
            name: permission.name,
            description: permission.description || '',
            visible_in_shop: permission.visible_in_shop !== false
        });
        setPermissionFormError('');
        setPermissionDialogOpen(true);
    };

    const handleSavePermission = () => {
        const req = editingPermission
            ? updatePermission(accessToken, editingPermission.id, permissionForm)
            : createPermission(accessToken, { ...permissionForm, application_id: id });

        req.then(() => { setPermissionDialogOpen(false); fetchPermissions(); })
           .catch(err => setPermissionFormError(err.response?.data?.message || 'Erreur lors de l\'enregistrement'));
    };

    const handleDeletePermission = (permission) => {
        if (!window.confirm(`Supprimer la permission "${permission.name}" ?`)) return;
        deletePermission(accessToken, permission.id).then(fetchPermissions).catch(() => setPermissionsError('Suppression impossible'));
    };

    // ── Load presentation image preview ─────────────────────────────────────────
    useEffect(() => {
        if (!id || !accessToken || !application.image) {
            setImagePreviewUrl(null);
            return;
        }
        let objectUrl = null;
        let cancelled = false;

        axios.get(`${API_URL}/api/applications/${id}/image`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            responseType: 'blob',
        })
            .then(res => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(res.data);
                setImagePreviewUrl(objectUrl);
            })
            .catch(err => console.error('Error loading application image:', err.message));

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [id, accessToken, application.image]);

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !id) return;

        setUploadingImage(true);
        setImageError(null);

        const formData = new FormData();
        formData.append('image', file);

        axios.post(`${API_URL}/api/applications/${id}/image`, formData, {
            headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'multipart/form-data' },
        })
            .then(res => {
                setApplication(prev => ({ ...prev, image: res.data.image ?? prev.image }));
            })
            .catch(err => setImageError(err.response?.data?.message || err.message || "Erreur lors de l'envoi de l'image"))
            .finally(() => setUploadingImage(false));
    };

    const handleImageDelete = () => {
        if (!id) return;

        setUploadingImage(true);
        setImageError(null);

        axios.delete(`${API_URL}/api/applications/${id}/image`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        })
            .then(() => {
                setApplication(prev => ({ ...prev, image: '' }));
            })
            .catch(err => setImageError(err.response?.data?.message || err.message || "Erreur lors de la suppression de l'image"))
            .finally(() => setUploadingImage(false));
    };

    // ── Export PDF (fiche tarifaire, remise ponctuelle non persistée) ────

    const handleGeneratePdf = async (discount, expirationDate, managementFeeLabel) => {
        setGeneratingPdf(true);
        setPdfError(null);

        // Open synchronously, still inside the click gesture — opening only
        // after the await below gets silently blocked as a popup in some
        // browsers (Firefox/Safari in particular).
        const printWindow = openBlankPrintWindow();
        if (!printWindow) {
            setPdfError("Le navigateur a bloqué l'ouverture de la fenêtre d'impression (pop-up).");
            setGeneratingPdf(false);
            return;
        }

        try {
            const plans = await getPricingPlans(accessToken, 'application', id);
            const activePlans = (plans || []).filter((p) => p.is_active !== false);
            const sorted = [...activePlans].sort((a, b) => Number(a.base_amount || 0) - Number(b.base_amount || 0));
            const mainPlan = sorted[0];
            const otherPlans = sorted.slice(1);

            const basePrice = mainPlan && Number(mainPlan.base_amount) ? Number(mainPlan.base_amount) : null;
            const discounted = applyDiscount(basePrice, discount);
            const discountLabel = buildDiscountLabel(discount);
            const mainPriceLabel = describePlanPrice(mainPlan, { ht: true });

            const priceCaption = discountLabel
                ? `Offre spéciale ${discountLabel}`
                : (mainPlan?.name || 'Tarif de départ');
            const priceAmount = discounted != null ? `${formatEUR(discounted)} HT` : mainPriceLabel;
            const originalPriceAmount = discounted != null ? `${formatEUR(basePrice)} HT` : null;

            const html = buildProductSheetHtml({
                documentTitle: `Tarification — ${application.name} — Alaïs-Studio`,
                eyebrow: application.category || 'Logiciel',
                productName: application.name,
                priceCaption,
                priceAmount,
                originalPriceAmount,
                sectionHeading: 'Ce qui est inclus',
                sectionSubtitle: application.shortDescription,
                features: application.features || [],
                options: otherPlans.map((p) => ({ label: p.name || 'Formule', priceLabel: describePlanPrice(p) })),
                notes: [buildExpirationLabel(expirationDate), managementFeeLabel].filter(Boolean),
            });

            writeAndPrint(printWindow, html);
            setPdfExportOpen(false);
        } catch {
            printWindow.close();
            setPdfError('Échec de la génération du PDF');
        } finally {
            setGeneratingPdf(false);
        }
    };

    const handleSave = () => {
        if (!id) { setIsEditing(false); return; }

        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        axios.put(`${API_URL}/api/applications/${id}`, {
            // Champs de base
            name:                   application.name,
            short_description:      application.shortDescription,
            long_description:       application.description,
            redirect_urls:          Array.isArray(application.redirect_urls)
                                        ? application.redirect_urls
                                        : (application.redirect_urls || '').split('\n').map(u => u.trim()).filter(Boolean),
            allow_all_redirect_url: application.allow_all_redirect_url,
            isActive:               application.isActive,
            is_first_party:         application.is_first_party,
            is_internal:            application.is_internal,
            auto_grant_on_signup:   application.auto_grant_on_signup,
            requires_consent:       application.requires_consent,
            image:                  application.image,
            icon:                   application.icon,
            // Informations générales
            url:                    application.url,
            version:                application.version,
            category:               application.category,
            tags:                   Array.isArray(application.tags) ? application.tags : [],
            features:               Array.isArray(application.features) ? application.features : [],
            // Informations techniques
            technical_info:         application.technicalInfo,
        }, { headers: { Authorization: `Bearer ${accessToken}` } })
        .then(() => {
            setIsEditing(false);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 4000);
        })
        .catch(err => {
            setSaveError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde');
        })
        .finally(() => setSaving(false));
    };

    const handleAddScreenshot = (event) => {
        const file = event.target.files[0];
        if (file) {
            const newScreenshot = {
                id: Date.now(),
                url: URL.createObjectURL(file),
                title: `Capture ${screenshots.length + 1}`
            };
            setScreenshots([...screenshots, newScreenshot]);
        }
    };

    const handleRemoveScreenshot = (screenshotId) => {
        setScreenshots(screenshots.filter(s => s.id !== screenshotId));
    };

    const handleInputChange = (field, value) => {
        setApplication(prev => {
            if (field.includes('.')) {
                const parts = field.split('.');
                if (parts.length === 2) {
                    const [parent, child] = parts;
                    return {
                        ...prev,
                        [parent]: {
                            ...prev[parent],
                            [child]: value
                        }
                    };
                } else if (parts.length === 3) {
                    const [parent, middle, child] = parts;
                    return {
                        ...prev,
                        [parent]: {
                            ...prev[parent],
                            [middle]: {
                                ...prev[parent]?.[middle],
                                [child]: value
                            }
                        }
                    };
                }
            }
            return {
                ...prev,
                [field]: value
            };
        });
    };

    const getSubscriptionColor = (subscription) => {
        switch (subscription) {
            case 'Enterprise': return 'error';
            case 'Premium': return 'warning';
            case 'Basic': return 'info';
            case 'Test': return 'secondary';
            default: return 'default';
        }
    };

    const getEnvironmentColor = (environment) => {
        switch (environment) {
            case 'Production': return { bg: '#dcfce7', color: '#166534' };
            case 'Preprod': return { bg: '#fef3c7', color: '#92400e' };
            case 'Test': return { bg: '#dbeafe', color: '#1e40af' };
            default: return { bg: '#f1f5f9', color: 'text.secondary' };
        }
    };

    const getFilteredClients = () => {
        if (clientEnvironmentFilter === 'all') {
            return application.linkedClients || [];
        }
        return application.linkedClients?.filter(client =>
            client.environment?.toLowerCase() === clientEnvironmentFilter.toLowerCase()
        ) || [];
    };

    const getEnvironmentStats = () => {
        const clients = application.linkedClients || [];
        return {
            all: clients.length,
            production: clients.filter(c => c.environment === 'Production').length,
            preprod: clients.filter(c => c.environment === 'Preprod').length,
            test: clients.filter(c => c.environment === 'Test').length
        };
    };

    // Fonctions pour les clients d'architecture
    const getArchClientEnvironmentStats = () => {
        const clients = application.technicalInfo?.clients || [];
        return {
            all: clients.length,
            production: clients.filter(c => c.environment === 'Production').length,
            preprod: clients.filter(c => c.environment === 'Preprod').length,
            test: clients.filter(c => c.environment === 'Test').length
        };
    };

    const getFilteredArchClients = () => {
        if (archClientEnvironmentFilter === 'all') {
            return application.technicalInfo?.clients || [];
        }
        return application.technicalInfo?.clients?.filter(client =>
            client.environment?.toLowerCase() === archClientEnvironmentFilter.toLowerCase()
        ) || [];
    };

    const openAddClientDialog = () => {
        setEditingClient(null);
        setClientForm({
            name: '',
            url: '',
            environment: 'Production',
            platform: 'Web',
            version: '',
            status: 'Actif'
        });
        setOpenClientDialog(true);
    };

    const openEditClientDialog = (client) => {
        setEditingClient(client);
        setClientForm({
            name: client.name,
            url: client.url,
            environment: client.environment,
            platform: client.platform,
            version: client.version,
            status: client.status
        });
        setOpenClientDialog(true);
    };

    const handleClientFormChange = (field, value) => {
        setClientForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveClient = () => {
        const newClient = {
            ...clientForm,
            id: editingClient ? editingClient.id : Date.now()
        };

        setApplication(prev => {
            const clients = prev.technicalInfo?.clients || [];
            if (editingClient) {
                // Modifier le client existant
                return {
                    ...prev,
                    technicalInfo: {
                        ...prev.technicalInfo,
                        clients: clients.map(client =>
                            client.id === editingClient.id ? newClient : client
                        )
                    }
                };
            } else {
                // Ajouter nouveau client
                return {
                    ...prev,
                    technicalInfo: {
                        ...prev.technicalInfo,
                        clients: [...clients, newClient]
                    }
                };
            }
        });

        setOpenClientDialog(false);
    };

    const handleDeleteClient = (clientId) => {
        setApplication(prev => {
            return {
                ...prev,
                technicalInfo: {
                    ...prev.technicalInfo,
                    clients: prev.technicalInfo?.clients?.filter(client => client.id !== clientId) || []
                }
            };
        });
    };

    // Fonctions pour les groupes de clients
    const openAddGroupDialog = () => {
        setEditingGroup(null);
        setGroupForm({
            name: '',
            description: '',
            color: 'primary.main'
        });
        setOpenGroupDialog(true);
    };

    const openEditGroupDialog = (group) => {
        setEditingGroup(group);
        setGroupForm({
            name: group.name,
            description: group.description,
            color: group.color
        });
        setOpenGroupDialog(true);
    };

    const handleGroupFormChange = (field, value) => {
        setGroupForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveGroup = () => {
        const newGroup = {
            ...groupForm,
            id: editingGroup ? editingGroup.id : Date.now()
        };

        setApplication(prev => {
            const groups = prev.clientGroups || [];
            if (editingGroup) {
                // Modifier le groupe existant
                return {
                    ...prev,
                    clientGroups: groups.map(group =>
                        group.id === editingGroup.id ? newGroup : group
                    )
                };
            } else {
                // Ajouter nouveau groupe
                return {
                    ...prev,
                    clientGroups: [...groups, newGroup]
                };
            }
        });

        setOpenGroupDialog(false);
    };

    const handleDeleteGroup = (groupId) => {
        setApplication(prev => {
            return {
                ...prev,
                clientGroups: prev.clientGroups?.filter(group => group.id !== groupId) || []
            };
        });
    };

    const handleAddCategory = (category) => {
        if (category && !application.categories.includes(category)) {
            setApplication(prev => ({
                ...prev,
                categories: [...prev.categories, category]
            }));
        }
    };

    const handleRemoveCategory = (category) => {
        setApplication(prev => ({
            ...prev,
            categories: prev.categories.filter(cat => cat !== category)
        }));
    };

        // Fonctions de liaison client <-> application
    const openLinkDialog = () => {
        if (!accessToken) return;
        setLinkClientForm({ id_client: '', dateDebut: '', dateFin: '' });
        setClientSearch('');
        // Charger tous les clients
        axios.get(`${API_URL}/api/clients`, { headers: { Authorization: `Bearer ${accessToken}` } })
            .then(res => setAllClients(Array.isArray(res.data) ? res.data : []))
            .catch(() => setAllClients([]));
        setOpenLinkClientDialog(true);
    };

    const handleLinkClient = () => {
        if (!accessToken || !linkClientForm.id_client) return;
        setLinkingClient(true);
        axios.post(
            `${API_URL}/api/applications/${id}/clients`,
            { id_client: linkClientForm.id_client, date_debut: linkClientForm.dateDebut || null, date_fin: linkClientForm.dateFin || null },
            { headers: { Authorization: `Bearer ${accessToken}` } }
        )
        .then(() => {
            setOpenLinkClientDialog(false);
            // Recharger la liste des clients
            return axios.get(`${API_URL}/api/applications/${id}/clients`, { headers: { Authorization: `Bearer ${accessToken}` } });
        })
        .then(res => setAppClients(Array.isArray(res.data) ? res.data : []))
        .catch(err => console.error('Erreur liaison client:', err.message))
        .finally(() => setLinkingClient(false));
    };

    const handleUnlinkClient = (id_client) => {
        if (!accessToken) return;
        axios.delete(
            `${API_URL}/api/applications/${id}/clients/${id_client}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        )
        .then(() => setAppClients(prev => prev.filter(c => c.id_client !== id_client)))
        .catch(err => console.error('Erreur dissociation client:', err.message));
    };

        // Fonctions de gestion des serveurs
    const openAddServerDialog = () => {
        setEditingServer(null);
        setServerForm({
            name: '',
            url: '',
            type: 'Production',
            status: 'Actif'
        });
        setOpenServerDialog(true);
    };

    const openEditServerDialog = (server) => {
        setEditingServer(server);
        setServerForm({
            name: server.name,
            url: server.url,
            type: server.type,
            status: server.status
        });
        setOpenServerDialog(true);
    };

    const handleServerFormChange = (field, value) => {
        setServerForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveServer = () => {
        const newServer = {
            ...serverForm,
            id: editingServer ? editingServer.id : Date.now()
        };

        setApplication(prev => {
            const servers = prev.technicalInfo?.servers || [];
            if (editingServer) {
                // Modifier le serveur existant
                return {
                    ...prev,
                    technicalInfo: {
                        ...prev.technicalInfo,
                        servers: servers.map(server =>
                            server.id === editingServer.id ? newServer : server
                        )
                    }
                };
            } else {
                // Ajouter nouveau serveur
                return {
                    ...prev,
                    technicalInfo: {
                        ...prev.technicalInfo,
                        servers: [...servers, newServer]
                    }
                };
            }
        });

        setOpenServerDialog(false);
    };

    const handleDeleteServer = (serverId) => {
        setApplication(prev => {
            return {
                ...prev,
                technicalInfo: {
                    ...prev.technicalInfo,
                    servers: prev.technicalInfo?.servers?.filter(server => server.id !== serverId) || []
                }
            };
        });
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            {/* Header moderne et épuré */}
            <Box sx={{
                bgcolor: 'background.paper',
                borderBottom: '1px solid', borderColor: 'divider',
                px: { xs: 2, sm: 3 },
                py: 2
            }}>
                <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
                        <IconButton
                            onClick={() => navigate('/applications')}
                            sx={{ color: 'text.secondary' }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h4" sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            flexGrow: 1
                        }}>
                            {application.name}
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {canDiscount && (
                                <Button
                                    variant="outlined"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => setPdfExportOpen(true)}
                                    sx={{ borderColor: 'divider', color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                                >
                                    Exporter en PDF
                                </Button>
                            )}
                            {canManageApp && (
                            <Button
                                variant={isEditing ? 'contained' : 'outlined'}
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : isEditing ? <SaveIcon /> : <EditIcon />}
                                onClick={isEditing ? handleSave : () => { setIsEditing(true); setSaveError(null); setSaveSuccess(false); }}
                                disabled={saving}
                                sx={{
                                    bgcolor: isEditing ? 'primary.main' : 'transparent',
                                    borderColor: 'divider',
                                    color: isEditing ? 'white' : 'text.secondary',
                                    '&:hover': {
                                        bgcolor: isEditing ? 'primary.dark' : 'action.hover'
                                    }
                                }}
                            >
                                {saving ? 'Sauvegarde...' : isEditing ? 'Sauvegarder' : 'Modifier'}
                            </Button>
                            )}
                            {application.url && (
                                <Button
                                    variant="outlined"
                                    startIcon={<LaunchIcon />}
                                    href={application.url}
                                    target="_blank"
                                    sx={{
                                        borderColor: 'primary.main',
                                        color: 'primary.main',
                                        '&:hover': {
                                            bgcolor: 'action.hover'
                                        }
                                    }}
                                >
                                    Ouvrir
                                </Button>
                            )}
                        </Stack>
                    </Stack>

                    {/* Alertes sauvegarde */}
                    {pdfError && (
                        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setPdfError(null)}>
                            {pdfError}
                        </Alert>
                    )}
                    {saveSuccess && (
                        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSaveSuccess(false)}>
                            Application sauvegardée avec succès.
                        </Alert>
                    )}
                    {saveError && (
                        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setSaveError(null)}>
                            {saveError}
                        </Alert>
                    )}

                    {/* Métadonnées */}
                    <Stack direction="row" spacing={3} sx={{ color: 'text.secondary', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">Version</Typography>
                            <Chip
                                label={application.version}
                                size="small"
                                sx={{
                                    bgcolor: 'action.hover',
                                    color: 'text.secondary',
                                    height: 20,
                                    fontSize: '0.75rem'
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">Statut</Typography>
                            <Chip
                                label={application.status}
                                size="small"
                                color={application.status === 'Actif' ? 'success' : 'warning'}
                                sx={{
                                    height: 20,
                                    fontSize: '0.75rem'
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">Type</Typography>
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                {application.type}
                            </Typography>
                        </Box>
                        {/* Indicateur app interne */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isEditing ? (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={application.is_internal}
                                            onChange={e => setApplication(prev => ({ ...prev, is_internal: e.target.checked }))}
                                            sx={{ '& .MuiSwitch-thumb': { bgcolor: application.is_internal ? '#1e3a5f' : undefined } }}
                                        />
                                    }
                                    label={<Typography variant="body2">Application interne</Typography>}
                                />
                            ) : application.is_internal ? (
                                <Chip
                                    label="Interne"
                                    size="small"
                                    sx={{ bgcolor: '#1e3a5f', color: '#fff', height: 20, fontSize: '0.75rem' }}
                                />
                            ) : null}
                        </Box>
                        {/* Octroi automatique à l'inscription */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isEditing ? (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={application.auto_grant_on_signup}
                                            onChange={e => setApplication(prev => ({ ...prev, auto_grant_on_signup: e.target.checked }))}
                                        />
                                    }
                                    label={<Typography variant="body2">Accès accordé automatiquement à l'inscription</Typography>}
                                />
                            ) : application.auto_grant_on_signup ? (
                                <Chip
                                    label="Auto-accordée à l'inscription"
                                    size="small"
                                    color="success"
                                    sx={{ height: 20, fontSize: '0.75rem' }}
                                />
                            ) : null}
                        </Box>
                    </Stack>
                </Box>
            </Box>

            {/* Contenu principal avec onglets */}
            <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
                {/* Navigation par onglets */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="Navigation de l'application"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                fontWeight: 500,
                                color: 'text.secondary',
                                minWidth: { xs: 'auto', md: 160 },
                                '&.Mui-selected': {
                                    color: 'text.primary',
                                    fontWeight: 600
                                }
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#3b82f6'
                            }
                        }}
                    >
                        <Tab label="Vue d'ensemble" />
                        <Tab label="Commercialisation" />
                        <Tab label="Media" />
                        <Tab label="Client" />
                        {canTechnical && <Tab label="Information technique" />}
                    </Tabs>
                </Box>

                {/* Onglet 1: Vue d'ensemble */}
                <TabPanel value={activeTab} index={0}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                        {/* Informations générales */}
                        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{
                                    mb: 3,
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    borderBottom: '1px solid', borderColor: 'divider',
                                    pb: 2
                                }}>
                                    Informations générales
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                    <Avatar
                                        variant="rounded"
                                        src={imagePreviewUrl || undefined}
                                        sx={{ width: 80, height: 80, bgcolor: 'action.hover' }}
                                    >
                                        {!imagePreviewUrl && <PhotoCameraIcon color="disabled" />}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            Image de présentation
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            Utilisée par l'e-boutique pour présenter l'application
                                        </Typography>
                                        {isEditing && (
                                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                                <Button
                                                    component="label"
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={uploadingImage ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                                                    disabled={uploadingImage || !id}
                                                >
                                                    {imagePreviewUrl ? "Changer l'image" : "Ajouter une image"}
                                                    <input
                                                        type="file"
                                                        accept="image/jpeg,image/png,image/gif,image/webp"
                                                        hidden
                                                        onChange={handleImageChange}
                                                    />
                                                </Button>
                                                {imagePreviewUrl && (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        startIcon={<DeleteIcon />}
                                                        disabled={uploadingImage}
                                                        onClick={handleImageDelete}
                                                    >
                                                        Supprimer
                                                    </Button>
                                                )}
                                            </Stack>
                                        )}
                                        {imageError && (
                                            <Alert severity="error" sx={{ mt: 1, py: 0 }} onClose={() => setImageError(null)}>
                                                {imageError}
                                            </Alert>
                                        )}
                                    </Box>
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Nom de l'application"
                                            value={application.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            disabled={!isEditing}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Version"
                                            value={application.version}
                                            onChange={(e) => handleInputChange('version', e.target.value)}
                                            disabled={!isEditing}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Description courte"
                                            value={application.shortDescription}
                                            onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                                            disabled={!isEditing}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Description détaillée"
                                            multiline
                                            rows={3}
                                            value={application.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                            disabled={!isEditing}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={!isEditing} size="small">
                                            <InputLabel>Type d'application</InputLabel>
                                            <Select
                                                value={application.type}
                                                onChange={(e) => handleInputChange('type', e.target.value)}
                                            >
                                                <MenuItem value="Client">Application Client</MenuItem>
                                                <MenuItem value="Interne">Application Interne</MenuItem>
                                                <MenuItem value="Service/API">Service/API</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth disabled={!isEditing} size="small">
                                            <InputLabel>Statut</InputLabel>
                                            <Select
                                                value={application.status}
                                                onChange={(e) => handleInputChange('status', e.target.value)}
                                            >
                                                <MenuItem value="Actif">Actif</MenuItem>
                                                <MenuItem value="En développement">En développement</MenuItem>
                                                <MenuItem value="Maintenance">Maintenance</MenuItem>
                                                <MenuItem value="Archivé">Archivé</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        </Grid>

                        {/* Catégories */}
                        <Grid item xs={12}>
                            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h6" sx={{
                                            fontWeight: 600,
                                            color: 'text.primary',
                                            borderBottom: '1px solid', borderColor: 'divider',
                                            pb: 2,
                                            flex: 1
                                        }}>
                                            Catégories d'application
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                        <Chip
                                            label={application.category}
                                            variant="filled"
                                            sx={{
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                        {application.categories?.filter(cat => cat !== application.category).map((category, index) => (
                                            <Chip
                                                key={index}
                                                label={category}
                                                variant="outlined"
                                                sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                                                onDelete={isEditing ? () => handleRemoveCategory(category) : undefined}
                                            />
                                        ))}
                                    </Box>
                                    {isEditing && (
                                        <Box sx={{ mt: 2 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => setOpenCategoryDialog(true)}
                                                sx={{
                                                    borderColor: 'primary.main',
                                                    color: 'primary.main',
                                                    '&:hover': { bgcolor: 'action.hover' }
                                                }}
                                            >
                                                Ajouter catégorie
                                            </Button>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Fonctionnalités principales */}
                        <Grid item xs={12}>
                            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{
                                        mb: 3,
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        borderBottom: '1px solid', borderColor: 'divider',
                                        pb: 2
                                    }}>
                                        Fonctionnalités principales
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                        {application.features?.map((feature, index) => (
                                            <Chip
                                                key={index}
                                                label={feature}
                                                variant="outlined"
                                                size="small"
                                                sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                                            />
                                        ))}
                                    </Box>
                                    {isEditing && (
                                        <TextField
                                            fullWidth
                                            label="Fonctionnalités (une par ligne)"
                                            multiline
                                            rows={3}
                                            value={application.features?.join('\n') || ''}
                                            onChange={(e) => handleInputChange('features', e.target.value.split('\n').filter(f => f.trim()))}
                                            size="small"
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Sidebar pour Vue d'ensemble */}
                        <Grid item xs={12} md={4}>

                            {/* Actions rapides */}
                            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{
                                        mb: 3,
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        borderBottom: '1px solid', borderColor: 'divider',
                                        pb: 2
                                    }}>
                                        Actions rapides
                                    </Typography>
                                    <Stack spacing={2}>
                                        {application.url && (
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<LaunchIcon />}
                                                href={application.url}
                                                target="_blank"
                                                sx={{
                                                    borderColor: 'primary.main',
                                                    color: 'primary.main',
                                                    '&:hover': { bgcolor: 'action.hover' }
                                                }}
                                            >
                                                {application.type === 'Service/API' ? 'Tester l\'API' : 'Ouvrir l\'app'}
                                            </Button>
                                        )}
                                        {application.technicalInfo?.documentation && (
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<LaunchIcon />}
                                                href={application.technicalInfo.documentation}
                                                target="_blank"
                                                sx={{
                                                    borderColor: '#8b5cf6',
                                                    color: '#8b5cf6',
                                                    '&:hover': { bgcolor: '#f3e8ff' }
                                                }}
                                            >
                                                Documentation
                                            </Button>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>


                        </Grid>
                    </Grid>
                                </TabPanel>

                {/* Onglet 2: Commercialisation */}
                <TabPanel value={activeTab} index={1}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{
                                mb: 3,
                                fontWeight: 600,
                                color: 'text.primary',
                                borderBottom: '1px solid', borderColor: 'divider',
                                pb: 2
                            }}>
                                Commercialisation
                            </Typography>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                La tarification de cette application (plans, paliers, grilles tarifaires, codes promo)
                                ainsi que celle de ses options se gèrent depuis une page dédiée.
                            </Alert>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<EuroIcon />}
                                onClick={() => navigate(`/applications/${id}/pricing`)}
                            >
                                Gérer la tarification
                            </Button>
                        </CardContent>
                    </Card>
                </TabPanel>

                {/* Onglet 3: Media */}
                <TabPanel value={activeTab} index={2}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            {/* Média et visuel */}
                        {application.hasInterface && (
                            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{
                                        mb: 3,
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        borderBottom: '1px solid', borderColor: 'divider',
                                        pb: 2
                                    }}>
                                        Média et visuel
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Logo/Icône</Typography>
                                                <Avatar
                                                    sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'action.hover' }}
                                                    src={application.images.logo}
                                                />
                                                {isEditing && (
                                                    <Button
                                                        size="small"
                                                        startIcon={<UploadIcon />}
                                                        sx={{ color: 'primary.main' }}
                                                    >
                                                        Modifier
                                                    </Button>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Captures d'écran</Typography>
                                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                                {screenshots.map((screenshot) => (
                                                    <Box
                                                        key={screenshot.id}
                                                        sx={{
                                                            position: 'relative',
                                                            width: 120,
                                                            height: 80,
                                                            borderRadius: 1,
                                                            overflow: 'hidden',
                                                            border: '1px solid', borderColor: 'divider',
                                                            bgcolor: 'background.default'
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={screenshot.url}
                                                            alt={screenshot.title}
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: 'none',
                                                                width: '100%',
                                                                height: '100%',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            <PhotoCameraIcon sx={{ color: 'text.disabled' }} />
                                                        </Box>
                                                        {isEditing && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRemoveScreenshot(screenshot.id)}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 4,
                                                                    right: 4,
                                                                    bgcolor: 'rgba(0,0,0,0.7)',
                                                                    color: 'white',
                                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                                                                }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                            {isEditing && (
                                                <Box>
                                                    <input
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        id="screenshot-upload"
                                                        type="file"
                                                        onChange={handleAddScreenshot}
                                                    />
                                                    <label htmlFor="screenshot-upload">
                                                        <Button
                                                            component="span"
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<AddIcon />}
                                                            sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                                                        >
                                                            Ajouter capture
                                                        </Button>
                                                    </label>
                                                </Box>
                                            )}
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Onglet 4: Client */}
                <TabPanel value={activeTab} index={3}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            {/* Statistiques par type */}
                            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{
                                        mb: 3, fontWeight: 600, color: 'text.primary',
                                        borderBottom: '1px solid', borderColor: 'divider', pb: 2
                                    }}>
                                        Répartition des clients
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {[
                                            { label: 'Total', value: appClients.length, bg: 'action.hover', color: 'text.secondary', border: '#cbd5e1' },
                                            { label: 'Particuliers', value: appClients.filter(c => c.type_client === 'individual').length, bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
                                            { label: 'Entreprises', value: appClients.filter(c => c.type_client === 'company').length, bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
                                        ].map(s => (
                                            <Grid item xs={6} md={4} key={s.label}>
                                                <Box sx={{ textAlign: 'center', p: 2, bgcolor: s.bg, borderRadius: 1, border: `1px solid ${s.border}` }}>
                                                    <Typography variant="h4" sx={{ fontWeight: 700, color: s.color }}>
                                                        {loadingClients ? '…' : s.value}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: s.color }}>
                                                        {s.label}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Clients liés (réels) */}
                            <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                            Clients abonnés ({appClients.length})
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={openLinkDialog}
                                        >
                                            Associer un client
                                        </Button>
                                    </Box>
                                    <Paper variant="outlined" sx={{ overflowX: 'hidden', width: '100%', border: '1px solid', borderColor: 'divider' }}>
                                        {loadingClients ? (
                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : appClients.length > 0 ? (
                                            <Box sx={{ overflowX: 'auto', width: '100%' }}>
                                                <TableContainer>
                                                    <Table size="small" sx={{ minWidth: { xs: '100%', sm: '650px' }, width: '100%' }}>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 600, color: 'text.primary', minWidth: '200px' }}>Client</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: 'text.primary', minWidth: '110px' }}>Type</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: 'text.primary', minWidth: '80px' }}>Utilisateurs</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: 'text.primary', minWidth: '110px' }}>Début</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: 'text.primary', minWidth: '110px' }}>Fin</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: 'text.primary', minWidth: '100px' }}>Actions</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {appClients.map((client) => {
                                                                const displayName = client.type_client === 'company'
                                                                    ? (client.raison_sociale || client.nom || '—')
                                                                    : (client.nom || client.raison_sociale || '—');
                                                                const typeLabel = client.type_client === 'company' ? 'Entreprise' : 'Particulier';
                                                                const typeColor = client.type_client === 'company' ? 'success' : 'info';
                                                                return (
                                                                    <TableRow key={client.id_client} hover>
                                                                        <TableCell>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <PeopleIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                                                                                <Box>
                                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                        {displayName}
                                                                                    </Typography>
                                                                                    {client.Utilisateurs?.[0] && (
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                            {client.Utilisateurs[0].prenom} {client.Utilisateurs[0].nom}
                                                                                        </Typography>
                                                                                    )}
                                                                                </Box>
                                                                            </Box>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Chip label={typeLabel} color={typeColor} size="small" />
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                {client.Utilisateurs?.length ?? 0}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2">
                                                                                {(() => {
                                                                                    const ca = client.ClientApplications?.find(ca => ca.key_application === id);
                                                                                    return ca?.date_debut ? new Date(ca.date_debut).toLocaleDateString('fr-FR') : '—';
                                                                                })()}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2">
                                                                                {(() => {
                                                                                    const ca = client.ClientApplications?.find(ca => ca.key_application === id);
                                                                                    return ca?.date_fin ? new Date(ca.date_fin).toLocaleDateString('fr-FR') : '—';
                                                                                })()}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => navigate(`/clients/${client.id_client}`)}
                                                                                    sx={{ color: 'primary.main', '&:hover': { backgroundColor: '#dbeafe' } }}
                                                                                    title="Gérer le client"
                                                                                >
                                                                                    <SettingsIcon fontSize="small" />
                                                                                </IconButton>
                                                                                {canDeleteApp && (
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={() => handleUnlinkClient(client.id_client)}
                                                                                        sx={{ color: 'error.main', '&:hover': { backgroundColor: '#fee2e2' } }}
                                                                                        title="Dissocier"
                                                                                    >
                                                                                        <LinkOffIcon fontSize="small" />
                                                                                    </IconButton>
                                                                                )}
                                                                            </Box>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        ) : (
                                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                                <Alert severity="info">Aucun client abonné à cette application.</Alert>
                                            </Box>
                                        )}
                                    </Paper>
                                </CardContent>
                            </Card>

                            {/* Relations (applications parentes / enfants) */}
                            {(appRelations.parent || appRelations.children?.length > 0) && (
                                <Card sx={{ mt: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
                                            Relations entre applications
                                        </Typography>
                                        {appRelations.parent && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Application parente</Typography>
                                                <Chip
                                                    label={appRelations.parent.name || appRelations.parent.client_id}
                                                    onClick={() => navigate(`/applications/${appRelations.parent.client_id}`)}
                                                    sx={{ bgcolor: '#f1f5f9', cursor: 'pointer' }}
                                                />
                                            </Box>
                                        )}
                                        {appRelations.children?.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Applications enfants</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {appRelations.children.map(child => (
                                                        <Chip
                                                            key={child.client_id}
                                                            label={child.name || child.client_id}
                                                            onClick={() => navigate(`/applications/${child.client_id}`)}
                                                            sx={{ bgcolor: '#f1f5f9', cursor: 'pointer' }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Onglet 5: Information technique */}
                <TabPanel value={activeTab} index={4}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            {/* Informations techniques */}
                            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{
                                        mb: 3,
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        borderBottom: '1px solid', borderColor: 'divider',
                                        pb: 2
                                    }}>
                                        Informations techniques
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="URL par défaut"
                                                value={application.url}
                                                onChange={(e) => handleInputChange('url', e.target.value)}
                                                disabled={!isEditing}
                                                size="small"
                                                helperText="Utilisée si aucune URL personnalisée n'est définie pour le client"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="API Endpoint"
                                                value={application.technicalInfo?.apiEndpoint || ''}
                                                onChange={(e) => handleInputChange('technicalInfo.apiEndpoint', e.target.value)}
                                                disabled={!isEditing}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Documentation"
                                                value={application.technicalInfo?.documentation || ''}
                                                onChange={(e) => handleInputChange('technicalInfo.documentation', e.target.value)}
                                                disabled={!isEditing}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth disabled={!isEditing} size="small">
                                                <InputLabel>Environnement</InputLabel>
                                                <Select
                                                    value={application.technicalInfo?.environment || 'Cloud'}
                                                    onChange={(e) => handleInputChange('technicalInfo.environment', e.target.value)}
                                                >
                                                    <MenuItem value="Cloud">Cloud</MenuItem>
                                                    <MenuItem value="Serveur dédié">Serveur dédié</MenuItem>
                                                    <MenuItem value="Hybride">Hybride</MenuItem>
                                                    <MenuItem value="Local">Local</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth disabled={!isEditing} size="small">
                                                <InputLabel>Architecture</InputLabel>
                                                <Select
                                                    value={application.technicalInfo?.architecture || 'single'}
                                                    onChange={(e) => handleInputChange('technicalInfo.architecture', e.target.value)}
                                                >
                                                    <MenuItem value="single">Application unique</MenuItem>
                                                    <MenuItem value="client-server">Client-Serveur</MenuItem>
                                                    <MenuItem value="microservices">Microservices</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Dépendances principales"
                                                value={application.technicalInfo?.dependencies?.join(', ') || ''}
                                                onChange={(e) => handleInputChange('technicalInfo.dependencies', e.target.value.split(', '))}
                                                disabled={!isEditing}
                                                size="small"
                                                helperText="Technologies et frameworks utilisés (séparés par des virgules)"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Permissions (droits de licence accordables par cette application) */}
                            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                            Permissions ({permissions.length})
                                        </Typography>
                                        {canTechnical && (
                                            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openCreatePermission}
                                                sx={{ borderColor: 'divider', color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
                                                Nouvelle permission
                                            </Button>
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Catalogue des droits que cette application peut accorder (ex: tracked_qrcodes,
                                        api_access). Utilisé par les plans tarifaires et par les licences client pour
                                        restreindre leurs sélecteurs à des clés existantes.
                                    </Typography>

                                    {permissionsError && <Alert severity="error" sx={{ mb: 2 }}>{permissionsError}</Alert>}

                                    {loadingPermissions ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
                                    ) : permissions.length === 0 ? (
                                        <Alert severity="info">Aucune permission définie pour cette application.</Alert>
                                    ) : (
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Clé</TableCell>
                                                        <TableCell>Nom</TableCell>
                                                        <TableCell>Description</TableCell>
                                                        <TableCell>E-boutique</TableCell>
                                                        {canTechnical && <TableCell>Actions</TableCell>}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {permissions.map((permission) => (
                                                        <TableRow key={permission.id} hover>
                                                            <TableCell sx={{ fontFamily: 'monospace' }}>{permission.key}</TableCell>
                                                            <TableCell>{permission.name}</TableCell>
                                                            <TableCell>{permission.description || '—'}</TableCell>
                                                            <TableCell>
                                                                {permission.visible_in_shop !== false ? (
                                                                    <Chip label="Visible" size="small" color="success" />
                                                                ) : (
                                                                    <Chip label="Masquée" size="small" variant="outlined" />
                                                                )}
                                                            </TableCell>
                                                            {canTechnical && (
                                                                <TableCell>
                                                                    <IconButton size="small" onClick={() => openEditPermission(permission)}><EditIcon fontSize="small" /></IconButton>
                                                                    <IconButton size="small" color="error" onClick={() => handleDeletePermission(permission)}><DeleteIcon fontSize="small" /></IconButton>
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Services connecteurs (registre de Connector) : uniquement sur la fiche CONNECTOR */}
                            {id === 'CONNECTOR' && <ConnectorServicesPanel accessToken={accessToken} />}

                            {/* Gestion des serveurs : architecture client-serveur, et toujours sur la fiche CONNECTOR
                                (le backoffice y lit l'adresse du backend, serveur de type « API » au statut « Actif ») */}
                            {(application.technicalInfo?.architecture === 'client-server' || id === 'CONNECTOR') && (
                                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Typography variant="h6" sx={{
                                                fontWeight: 600,
                                                color: 'text.primary'
                                            }}>
                                                Serveurs disponibles ({application.technicalInfo?.servers?.length || 0})
                                            </Typography>
                                            {isEditing && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                                                    onClick={openAddServerDialog}
                                                >
                                                    Ajouter serveur
                                                </Button>
                                            )}
                                        </Box>

                                        {application.technicalInfo?.servers?.length > 0 ? (
                                            <Grid container spacing={2}>
                                                {application.technicalInfo.servers.map((server) => (
                                                    <Grid item xs={12} md={6} key={server.id}>
                                                        <Card variant="outlined" sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                                                            <CardContent sx={{ p: 2 }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                                {server.name}
                                                                            </Typography>
                                                                            <Chip
                                                                                label={server.status}
                                                                                size="small"
                                                                                color={server.status === 'Actif' ? 'success' : 'warning'}
                                                                                sx={{
                                                                                    height: 20
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                        <Typography variant="body2" sx={{ color: 'primary.main', mb: 1, wordBreak: 'break-all' }}>
                                                                            {server.url}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                            Type: {server.type}
                                                                        </Typography>
                                                                    </Box>
                                                                    {isEditing && (
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => openEditServerDialog(server)}
                                                                                sx={{ color: 'primary.main' }}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleDeleteServer(server.id)}
                                                                                sx={{ color: 'error.main' }}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                                {!isEditing && (
                                                                    <Button
                                                                        fullWidth
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<LaunchIcon />}
                                                                        href={server.url}
                                                                        target="_blank"
                                                                        sx={{
                                                                            borderColor: 'primary.main',
                                                                            color: 'primary.main',
                                                                            '&:hover': { bgcolor: 'action.hover' }
                                                                        }}
                                                                    >
                                                                        Accéder au serveur
                                                                    </Button>
                                                                )}
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        ) : (
                                            <Alert severity="info">
                                                <Typography variant="body2">
                                                    Aucun serveur configuré. {isEditing && 'Cliquez sur "Ajouter serveur" pour en créer un.'}
                                                </Typography>
                                            </Alert>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Gestion des clients d'architecture pour architecture client-serveur */}
                            {application.technicalInfo?.architecture === 'client-server' && (
                                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Typography variant="h6" sx={{
                                                fontWeight: 600,
                                                color: 'text.primary'
                                            }}>
                                                Applications clientes ({getFilteredArchClients().length})
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {isEditing && (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<AddIcon />}
                                                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, mr: 1 }}
                                                        onClick={openAddClientDialog}
                                                    >
                                                        Ajouter client
                                                    </Button>
                                                )}
                                                <Button
                                                    size="small"
                                                    variant={archClientEnvironmentFilter === 'all' ? 'contained' : 'outlined'}
                                                    onClick={() => setArchClientEnvironmentFilter('all')}
                                                    sx={{
                                                        bgcolor: archClientEnvironmentFilter === 'all' ? '#3b82f6' : 'transparent',
                                                        borderColor: 'primary.main',
                                                        color: archClientEnvironmentFilter === 'all' ? 'white' : '#3b82f6',
                                                        '&:hover': {
                                                            bgcolor: archClientEnvironmentFilter === 'all' ? '#2563eb' : '#dbeafe'
                                                        }
                                                    }}
                                                >
                                                    Tous
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={archClientEnvironmentFilter === 'production' ? 'contained' : 'outlined'}
                                                    onClick={() => setArchClientEnvironmentFilter('production')}
                                                    sx={{
                                                        bgcolor: archClientEnvironmentFilter === 'production' ? '#16a34a' : 'transparent',
                                                        borderColor: '#16a34a',
                                                        color: archClientEnvironmentFilter === 'production' ? 'white' : '#16a34a',
                                                        '&:hover': {
                                                            bgcolor: archClientEnvironmentFilter === 'production' ? '#15803d' : '#dcfce7'
                                                        }
                                                    }}
                                                >
                                                    Production
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={archClientEnvironmentFilter === 'preprod' ? 'contained' : 'outlined'}
                                                    onClick={() => setArchClientEnvironmentFilter('preprod')}
                                                    sx={{
                                                        bgcolor: archClientEnvironmentFilter === 'preprod' ? '#d97706' : 'transparent',
                                                        borderColor: '#d97706',
                                                        color: archClientEnvironmentFilter === 'preprod' ? 'white' : '#d97706',
                                                        '&:hover': {
                                                            bgcolor: archClientEnvironmentFilter === 'preprod' ? '#b45309' : '#fef3c7'
                                                        }
                                                    }}
                                                >
                                                    Preprod
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={archClientEnvironmentFilter === 'test' ? 'contained' : 'outlined'}
                                                    onClick={() => setArchClientEnvironmentFilter('test')}
                                                    sx={{
                                                        bgcolor: archClientEnvironmentFilter === 'test' ? '#2563eb' : 'transparent',
                                                        borderColor: '#2563eb',
                                                        color: archClientEnvironmentFilter === 'test' ? 'white' : '#2563eb',
                                                        '&:hover': {
                                                            bgcolor: archClientEnvironmentFilter === 'test' ? '#1d4ed8' : '#dbeafe'
                                                        }
                                                    }}
                                                >
                                                    Test
                                                </Button>
                                            </Box>
                                        </Box>

                                        {getFilteredArchClients().length > 0 ? (
                                            <Grid container spacing={2}>
                                                {getFilteredArchClients().map((client) => (
                                                    <Grid item xs={12} md={6} key={client.id}>
                                                        <Card variant="outlined" sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                                                            <CardContent sx={{ p: 2 }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                                {client.name}
                                                                            </Typography>
                                                                            <Chip
                                                                                label={client.environment}
                                                                                size="small"
                                                                                color={getEnvironmentColor(client.environment)}
                                                                                sx={{
                                                                                    height: 20
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                        <Typography variant="body2" sx={{ color: 'primary.main', mb: 1, wordBreak: 'break-all' }}>
                                                                            {client.url}
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                                Plateforme: {client.platform}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                                • Version: {client.version}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Chip
                                                                            label={client.status}
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: client.status === 'Actif' ? '#dcfce7' : '#fef3c7',
                                                                                color: client.status === 'Actif' ? '#166534' : '#92400e'
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    {isEditing && (
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => openEditClientDialog(client)}
                                                                                sx={{ color: 'primary.main' }}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleDeleteClient(client.id)}
                                                                                sx={{ color: 'error.main' }}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                                {!isEditing && (
                                                                    <Button
                                                                        fullWidth
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<LaunchIcon />}
                                                                        href={client.url}
                                                                        target="_blank"
                                                                        sx={{
                                                                            borderColor: 'primary.main',
                                                                            color: 'primary.main',
                                                                            '&:hover': { bgcolor: 'action.hover' }
                                                                        }}
                                                                    >
                                                                        Ouvrir l'application
                                                                    </Button>
                                                                )}
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        ) : (
                                            <Alert severity="info">
                                                <Typography variant="body2">
                                                    {archClientEnvironmentFilter === 'all'
                                                        ? 'Aucune application cliente configurée. '
                                                        : `Aucune application cliente en environnement ${archClientEnvironmentFilter}. `
                                                    }
                                                    {isEditing && 'Cliquez sur "Ajouter client" pour en créer une.'}
                                                </Typography>
                                            </Alert>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>
                    </Grid>
                </TabPanel>

            {/* Dialog de gestion des serveurs */}
            <Dialog open={openServerDialog} onClose={() => setOpenServerDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ComputerIcon sx={{ mr: 1, color: 'primary.main' }} />
                        {editingServer ? 'Modifier le serveur' : 'Ajouter un nouveau serveur'}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nom du serveur"
                                    value={serverForm.name}
                                    onChange={(e) => handleServerFormChange('name', e.target.value)}
                                    required
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="URL du serveur"
                                    value={serverForm.url}
                                    onChange={(e) => handleServerFormChange('url', e.target.value)}
                                    required
                                    size="small"
                                    helperText="Ex: https://server.example.com"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Type de serveur</InputLabel>
                                    <Select
                                        value={serverForm.type}
                                        onChange={(e) => handleServerFormChange('type', e.target.value)}
                                    >
                                        <MenuItem value="Production">Production</MenuItem>
                                        <MenuItem value="Test">Test</MenuItem>
                                        <MenuItem value="Développement">Développement</MenuItem>
                                        <MenuItem value="Backup">Backup</MenuItem>
                                        <MenuItem value="API">API</MenuItem>
                                    </Select>
                                    {id === 'CONNECTOR' && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Connector : le backoffice utilise le premier serveur de type « API » au statut « Actif ».
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Statut</InputLabel>
                                    <Select
                                        value={serverForm.status}
                                        onChange={(e) => handleServerFormChange('status', e.target.value)}
                                    >
                                        <MenuItem value="Actif">Actif</MenuItem>
                                        <MenuItem value="Inactif">Inactif</MenuItem>
                                        <MenuItem value="Maintenance">Maintenance</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenServerDialog(false)}>
                        Annuler
                    </Button>
                    <Button
                        onClick={handleSaveServer}
                        variant="contained"
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                        disabled={!serverForm.name || !serverForm.url}
                    >
                        {editingServer ? 'Modifier' : 'Ajouter'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de gestion des clients d'architecture */}
            <Dialog open={openClientDialog} onClose={() => setOpenClientDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ComputerIcon sx={{ mr: 1, color: 'primary.main' }} />
                        {editingClient ? 'Modifier l\'application cliente' : 'Ajouter une nouvelle application cliente'}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nom de l'application cliente"
                                    value={clientForm.name}
                                    onChange={(e) => handleClientFormChange('name', e.target.value)}
                                    required
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="URL de l'application"
                                    value={clientForm.url}
                                    onChange={(e) => handleClientFormChange('url', e.target.value)}
                                    required
                                    size="small"
                                    helperText="Ex: https://app.example.com ou https://apps.apple.com/app/..."
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Environnement</InputLabel>
                                    <Select
                                        value={clientForm.environment}
                                        onChange={(e) => handleClientFormChange('environment', e.target.value)}
                                    >
                                        <MenuItem value="Production">Production</MenuItem>
                                        <MenuItem value="Preprod">Preprod</MenuItem>
                                        <MenuItem value="Test">Test</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Plateforme</InputLabel>
                                    <Select
                                        value={clientForm.platform}
                                        onChange={(e) => handleClientFormChange('platform', e.target.value)}
                                    >
                                        <MenuItem value="Web">Web</MenuItem>
                                        <MenuItem value="iOS">iOS</MenuItem>
                                        <MenuItem value="Android">Android</MenuItem>
                                        <MenuItem value="Desktop">Desktop</MenuItem>
                                        <MenuItem value="API">API</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Version"
                                    value={clientForm.version}
                                    onChange={(e) => handleClientFormChange('version', e.target.value)}
                                    size="small"
                                    helperText="Ex: 2.1.0 ou 2.2.0-beta"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Statut</InputLabel>
                                    <Select
                                        value={clientForm.status}
                                        onChange={(e) => handleClientFormChange('status', e.target.value)}
                                    >
                                        <MenuItem value="Actif">Actif</MenuItem>
                                        <MenuItem value="Inactif">Inactif</MenuItem>
                                        <MenuItem value="Maintenance">Maintenance</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenClientDialog(false)}>
                        Annuler
                    </Button>
                    <Button
                        onClick={handleSaveClient}
                        variant="contained"
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                        disabled={!clientForm.name || !clientForm.url}
                    >
                        {editingClient ? 'Modifier' : 'Ajouter'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog d'ajout de catégorie */}
            <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                        Ajouter une catégorie
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Sélectionner une catégorie</InputLabel>
                            <Select
                                value=""
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleAddCategory(e.target.value);
                                        setOpenCategoryDialog(false);
                                    }
                                }}
                            >
                                <MenuItem value="Productivité">Productivité</MenuItem>
                                <MenuItem value="Communication">Communication</MenuItem>
                                <MenuItem value="E-commerce">E-commerce</MenuItem>
                                <MenuItem value="Finances">Finances</MenuItem>
                                <MenuItem value="Design">Design</MenuItem>
                                <MenuItem value="Développement">Développement</MenuItem>
                                <MenuItem value="Analytics">Analytics</MenuItem>
                                <MenuItem value="CRM">CRM</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenCategoryDialog(false)}>
                        Annuler
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de gestion des groupes de clients */}
            <Dialog open={openGroupDialog} onClose={() => setOpenGroupDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                        {editingGroup ? 'Modifier le groupe' : 'Ajouter un nouveau groupe'}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nom du groupe"
                                    value={groupForm.name}
                                    onChange={(e) => handleGroupFormChange('name', e.target.value)}
                                    required
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={groupForm.description}
                                    onChange={(e) => handleGroupFormChange('description', e.target.value)}
                                    multiline
                                    rows={2}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Couleur</InputLabel>
                                    <Select
                                        value={groupForm.color}
                                        onChange={(e) => handleGroupFormChange('color', e.target.value)}
                                        renderValue={(value) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    bgcolor: value
                                                }} />
                                                <Typography>Couleur sélectionnée</Typography>
                                            </Box>
                                        )}
                                    >
                                        <MenuItem value="#3b82f6">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                                Bleu
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="#10b981">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#10b981' }} />
                                                Vert
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="#f59e0b">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                                                Orange
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="#8b5cf6">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#8b5cf6' }} />
                                                Violet
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="#ef4444">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'error.main' }} />
                                                Rouge
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenGroupDialog(false)}>
                        Annuler
                    </Button>
                    <Button
                        onClick={handleSaveGroup}
                        variant="contained"
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                        disabled={!groupForm.name}
                    >
                        {editingGroup ? 'Modifier' : 'Ajouter'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Dialog: Associer un client à cette application */}
            <Dialog open={openLinkClientDialog} onClose={() => setOpenLinkClientDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Associer un client
                    <IconButton onClick={() => setOpenLinkClientDialog(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Rechercher un client"
                                value={clientSearch}
                                onChange={(e) => setClientSearch(e.target.value)}
                                size="small"
                                placeholder="Nom, raison sociale..."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Client</InputLabel>
                                <Select
                                    value={linkClientForm.id_client}
                                    onChange={(e) => setLinkClientForm(prev => ({ ...prev, id_client: e.target.value }))}
                                    label="Client"
                                >
                                    {allClients
                                        .filter(cl => {
                                            const name = cl.type_client === 'company'
                                                ? (cl.raison_sociale || cl.nom || '')
                                                : (cl.nom || cl.raison_sociale || '');
                                            return name.toLowerCase().includes(clientSearch.toLowerCase());
                                        })
                                        .map(cl => {
                                            const name = cl.type_client === 'company'
                                                ? (cl.raison_sociale || cl.nom || `Client #${cl.id_client}`)
                                                : (cl.nom || cl.raison_sociale || `Client #${cl.id_client}`);
                                            const alreadyLinked = appClients.some(ac => ac.id_client === cl.id_client);
                                            return (
                                                <MenuItem key={cl.id_client} value={cl.id_client} disabled={alreadyLinked}>
                                                    {name} {alreadyLinked ? '(déjà associé)' : ''}
                                                </MenuItem>
                                            );
                                        })
                                    }
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Date de début"
                                type="date"
                                value={linkClientForm.dateDebut}
                                onChange={(e) => setLinkClientForm(prev => ({ ...prev, dateDebut: e.target.value }))}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Date de fin (optionnel)"
                                type="date"
                                value={linkClientForm.dateFin}
                                onChange={(e) => setLinkClientForm(prev => ({ ...prev, dateFin: e.target.value }))}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenLinkClientDialog(false)}>Annuler</Button>
                    <Button
                        onClick={handleLinkClient}
                        variant="contained"
                        color="primary"
                        disabled={!linkClientForm.id_client || linkingClient}
                        startIcon={linkingClient ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {linkingClient ? 'Enregistrement...' : 'Associer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Permission */}
            <Dialog open={permissionDialogOpen} onClose={() => setPermissionDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingPermission ? 'Modifier la permission' : 'Nouvelle permission'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {permissionFormError && <Grid item xs={12}><Alert severity="error">{permissionFormError}</Alert></Grid>}
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Clé technique (ex: export_pdf)"
                                value={permissionForm.key} onChange={(e) => setPermissionForm(f => ({ ...f, key: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Nom" value={permissionForm.name}
                                onChange={(e) => setPermissionForm(f => ({ ...f, name: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" multiline rows={2} label="Description"
                                value={permissionForm.description} onChange={(e) => setPermissionForm(f => ({ ...f, description: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={permissionForm.visible_in_shop}
                                    onChange={(e) => setPermissionForm(f => ({ ...f, visible_in_shop: e.target.checked }))} />}
                                label="Visible dans l'e-boutique"
                            />
                            <Typography variant="caption" color="text.secondary" display="block">
                                Seul le nom est montré côté e-boutique (jamais la clé). Désactivez pour un droit
                                purement technique que le client ne doit pas voir, tout en gardant la possibilité
                                de l'accorder via un plan tarifaire.
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPermissionDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleSavePermission} disabled={!permissionForm.key || !permissionForm.name}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {editingPermission ? 'Modifier' : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>

            <PdfExportDialog
                open={pdfExportOpen}
                onClose={() => setPdfExportOpen(false)}
                onGenerate={handleGeneratePdf}
                generating={generatingPdf}
                defaultManagementFeeLabel={defaultManagementFeeLabel}
            />

            </Box>
        </Box>
    );
}
